import React, {
  useState, useRef, useEffect
} from 'react'
import ReactSVG from 'react-svg'
import debounce from 'lodash.debounce'
import { propType } from 'graphql-anywhere'
import classNames from 'classnames'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'

import ModalContainer from '../../components/modal-container'
import CONTENT_ITEM_FRAGMENT from '../../../graphql/fragments/content-item.gql'
import styles from './detail-rate-modal.css'
import { useUserRatingQuery, useUserRatingMutation } from './user-rating-query-mutation'
import closeIcon from '../../../images/close-icon-no-color.svg'
import { UserRatingScale, getRatingValueToUpdate } from './shared'
import DetailRateButton from './detail-rate-button'
import RateOption from './rate-option'
import { getModalLocation } from '../../lib/modal'
import { MODALS } from '../../constants'

/**
 * Contains the rate button(`DetailRateButton`) and rate modal on show page
 * * contentItem is series or movie
 * * do not show the rate button and modal when movie or show is coming soon
 */
const DetailRateButtonAndModal = ({
  contentItem, isAuthenticated
}) => {
  if (!contentItem) return null
  if (contentItem.isComingSoon) return null

  const history = useHistory()
  const [userRating, setRating] = useState(null)
  const [isModalVisible, setModalVisible] = useState(false)
  const rateButtonRef = useRef(null) // DetailRateButton
  const [modalPosition, setModalPosition] = useState({ top: '0px', left: '0px' })
  const [isRateOptionsCentre, setRateOptionsCentre] = useState(true) // whether rateOptionsRow is centre or left aligned to the close icon
  const rateOptionsPushLeftDistance = 115 // adjust according to the width of rateOptionsRow
  const estimatedRateOptionsHeight = 100 // adjust according to the height of rateOptionsRow

  // fetch userRating and update state
  useUserRatingQuery({
    contentItemId: contentItem.id,
    skip: !isAuthenticated,
    setRating
  })

  // call mutation `updateUserRating` when RateOption is clicked, and update state after mutation is successful
  const { userRatingMutation } = useUserRatingMutation({ skip: !isAuthenticated, setRating })

  /**
   * When `RateOption` clicked, close rate modal, call updateUserRating mutation, and update state
   * @param {'NOT_MY_THING' | 'SOLID_WATCH' | 'TOP_PICK'} ratingValueClicked - ref to {@link UserRatingScale}
   */
  const onRateOptionClicked = ratingValueClicked => {
    toggleModalVisibility()
    userRatingMutation({
      variables: {
        input: {
          contentItemId: contentItem.id,
          userRating: getRatingValueToUpdate(userRating, ratingValueClicked)
        }
      }
    })
  }

  // When `DetailRateButton` clicked
  const onRateButtonClicked = () => {
    // require login for rating content
    if (!isAuthenticated) {
      history.push(getModalLocation(location, MODALS.qsParams.login))
      return
    }
    toggleModalVisibility()
  }

  const updateModalPosition = () => {
    if (rateButtonRef.current) {
      const rateButtonRect = rateButtonRef.current.getBoundingClientRect()
      // position A. place the modal in the middle of the screen, used when the rate button is off the screen(have to scroll to see it).
      // Because react modal scrolls the screen back to top, which makes the button and modal invisible if using position B.
      if (rateButtonRect.bottom + window.scrollY + estimatedRateOptionsHeight > window.innerHeight) {
        setModalPosition({
          top: '50%',
          left: '50%'
        })
      } else {
        setModalPosition({ // position B. stick modal to the rate button
          top: `${rateButtonRect.top + window.scrollY}px`,
          left: `${rateButtonRect.left}px`
        })
      }
      // when there's enough space from screen left,
      // we can push the rateOptionsRow left to make it look like center aligned to the rate button
      const distanceFromScreenLeft = rateButtonRect.left
      if (distanceFromScreenLeft > rateOptionsPushLeftDistance) {
        setRateOptionsCentre(true)
      } else {
        setRateOptionsCentre(false)
      }
    }
  }

  // update position during window resize
  useEffect(() => {
    const handleResize = debounce(() => {
      updateModalPosition()
    }, 0) // increase debounce if there's performance issue
    if (isModalVisible) {
      window.addEventListener('resize', handleResize)
    }
    return () => {
      window.removeEventListener('resize', handleResize)
      handleResize.cancel()
    }
  }, [isModalVisible])

  const toggleModalVisibility = () => {
    setModalVisible(prevVisible => {
      const newVisibility = !prevVisible
      if (newVisibility) {
        updateModalPosition()
      }
      return newVisibility
    })
  }

  return (
    <>
      {/* rate button - hide it when the modal is shown */}
      <DetailRateButton
        contentItem={contentItem}
        userRating={userRating}
        onClick={onRateButtonClicked}
        ref={rateButtonRef}
        isModalVisible={isModalVisible}
      />
      {/* rate modal - close icon, "RATE" label, and 3 rate options */}
      {isModalVisible && (
        <ModalContainer
          shouldCloseOnOverlayClick
          noBackgroundColor
          onRequestClose={toggleModalVisibility}
        >
          <div
            className={styles.detailRateModal}
            style={{
              top: modalPosition.top,
              left: modalPosition.left
            }}
          >
            {/* close icon and "RATE" label */}
            <div className={styles.rateHeaderRow}>
              <div
                className={styles.closeIconWrapper}
                onClick={() => setModalVisible(false)}
              >
                <ReactSVG
                  src={closeIcon}
                  beforeInjection={svg => {
                    svg.setAttribute(
                      'class',
                      classNames(styles.closeIcon)
                    )
                  }}
                />
              </div>
              <span className={styles.rateText}>RATE</span>
            </div>
            {/* 3 rate options */}
            <div
              className={styles.rateOptionsRow}
              style={{
                marginLeft: isRateOptionsCentre ? rateOptionsPushLeftDistance * -1 : 0
              }}
            >
              <RateOption
                defaultScale={UserRatingScale.NOT_MY_THING}
                userRating={userRating}
                onRateClick={onRateOptionClicked}
              />
              <RateOption
                defaultScale={UserRatingScale.SOLID_WATCH}
                userRating={userRating}
                onRateClick={onRateOptionClicked}
              />
              <RateOption
                defaultScale={UserRatingScale.TOP_PICK}
                userRating={userRating}
                onRateClick={onRateOptionClicked}
              />
            </div>
          </div>
        </ModalContainer>
      )}
    </>
  )
}

DetailRateButtonAndModal.propTypes = {
  contentItem: propType(CONTENT_ITEM_FRAGMENT).isRequired,
  isAuthenticated: PropTypes.bool.isRequired
}

export default DetailRateButtonAndModal
